import { ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { NotificationService } from 'src/app/services/notification.service';
import { Client, ClientCallHistoryItem } from 'src/typings';
import { ClientService } from '../../client.service';

@Component({
  selector: 'app-client-call-history',
  templateUrl: './call-history.component.html',
  styleUrls: ['./call-history.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default
})
export class CallHistoryComponent implements OnChanges, OnDestroy {
  @Input() client: Client | undefined;

  calls: ClientCallHistoryItem[] = [];
  loading = false;
  loaded = false;
  error = '';
  private revision = 0;
  ngOnDestroy() { this.revision++; }
  periodLabel = '';

  readonly displayedColumns = ['summary', 'clientPhone', 'managerNumber', 'result', 'recording'];

  constructor(
    private readonly clientService: ClientService,
    private readonly notificationService: NotificationService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.client?.currentValue?.id && changes.client?.previousValue?.id !== this.client?.id) {
      this.calls = []; this.loaded = false; this.loading = false; this.revision++;
      this.load();
    }
  }

  async load() {
    if (!this.client?.id || this.loading) return;

    this.loading = true; this.error = '';
    const revision = ++this.revision;
    try {
      const response = await firstValueFrom(
        this.clientService.getCallHistory(this.client.id, { days: 90, limit: 100 })
      );
      if (revision !== this.revision) return;
      this.calls = response.items || [];
      this.periodLabel = this.buildPeriodLabel(response.from, response.to);
      this.loaded = true;
    } catch (error: unknown) {
      if (revision === this.revision) this.error = this.getErrorMessage(error, 'Не удалось загрузить звонки');
    } finally {
      if (revision === this.revision) this.loading = false;
    }
  }

  trackByCall(_: number, call: ClientCallHistoryItem) {
    return call.id;
  }

  getDirectionLabel(call: ClientCallHistoryItem) {
    if (call.direction === 'to_client') return 'Звонили клиенту';
    if (call.direction === 'from_client') return 'Клиент звонил';
    return 'Звонок';
  }

  getDirectionClass(call: ClientCallHistoryItem) {
    return call.direction === 'to_client'
      ? 'directionChip directionChipOut'
      : 'directionChip directionChipIn';
  }

  getStatusLabel(call: ClientCallHistoryItem) {
    const normalized = this.normalizeStatusKey(call.status || call.statusLabel);
    const labels: Record<string, string> = {
      ANSWERED: 'Отвечен',
      ANSWER: 'Отвечен',
      SUCCESS: 'Отвечен',
      CONNECTED: 'Отвечен',
      CLIENT_ANSWER: 'Клиент ответил',
      CLIENT_ANSWERED: 'Клиент ответил',
      MANAGER_ANSWER: 'Менеджер ответил',
      MANAGER_ANSWERED: 'Менеджер ответил',
      NO_ANSWER: 'Без ответа',
      NOANSWER: 'Без ответа',
      NOT_ANSWERED: 'Без ответа',
      MISSED: 'Без ответа',
      CLIENT_NO_ANSWER: 'Клиент не ответил',
      CLIENT_NOANSWER: 'Клиент не ответил',
      CLIENT_NOT_ANSWERED: 'Клиент не ответил',
      MANAGER_NO_ANSWER: 'Менеджер не ответил',
      MANAGER_NOANSWER: 'Менеджер не ответил',
      MANAGER_NOT_ANSWERED: 'Менеджер не ответил',
      BUSY: 'Занято',
      CLIENT_BUSY: 'Клиент занят',
      MANAGER_BUSY: 'Менеджер занят',
      FAILED: 'Ошибка',
      CANCEL: 'Сброшен',
      CANCELED: 'Сброшен',
      CANCELLED: 'Сброшен',
      CONGESTION: 'Сеть занята',
      FAILED_FORBIDDEN_DESTINATION: 'Запрещённое направление',
      NO_FORWARD: 'Переадресация не сработала',
      REPEATED: 'Повторный звонок',
      ОТВЕЧЕН: 'Отвечен',
      БЕЗ_ОТВЕТА: 'Без ответа',
      ЗАНЯТО: 'Занято',
      СБРОШЕН: 'Сброшен'
    };

    return labels[normalized] || call.statusLabel || 'Другой статус';
  }

  getStatusClass(call: ClientCallHistoryItem) {
    const normalized = this.normalizeStatusKey(call.status || call.statusLabel);
    if (['ANSWERED', 'ANSWER', 'SUCCESS', 'CONNECTED', 'ОТВЕЧЕН'].includes(normalized)) {
      return 'statusChip statusChipAnswered';
    }
    if (normalized.includes('BUSY') || normalized === 'ЗАНЯТО') {
      return 'statusChip statusChipBusy';
    }
    if (normalized.includes('FAILED') || normalized.includes('CANCEL') || normalized === 'СБРОШЕН') {
      return 'statusChip statusChipError';
    }
    return 'statusChip statusChipMissed';
  }

  getClientPhoneLabel(call: ClientCallHistoryItem) {
    return this.formatPhone(call.clientPhone) || 'Номер клиента не определён';
  }

  getManagerLabel(call: ClientCallHistoryItem) {
    const value = String(call.managerNumber || '').trim();
    if (!value) return 'Линия не определена';

    const phone = this.formatPhone(value);
    if (phone) return phone;

    const angleMatch = value.match(/<([^>]+)>/);
    const endpoint = (angleMatch?.[1] || value)
      .replace(/^sip\//i, '')
      .replace(/^["']+|["']+$/g, '')
      .replace(/[_-]+/g, ' ')
      .trim();

    return endpoint ? `Линия ${endpoint}` : 'Линия не определена';
  }

  getRecordingLabel(call: ClientCallHistoryItem) {
    return call.hasRecording ? 'Запись разговора' : 'Нет записи';
  }

  private normalizeStatusKey(value?: string | null) {
    return String(value || '')
      .trim()
      .toUpperCase()
      .replace(/[^\p{L}\p{N}]+/gu, '_')
      .replace(/^_+|_+$/g, '');
  }

  private formatPhone(value?: string | null) {
    const digits = String(value || '').replace(/\D/g, '');
    if (!digits) return '';

    const local = digits.length === 12 && digits.startsWith('38') ? digits.slice(2) : digits;
    if (/^0\d{9}$/.test(local)) {
      return `+38 (${local.slice(0, 3)}) ${local.slice(3, 6)} ${local.slice(6, 8)} ${local.slice(8)}`;
    }

    return /^\d{10,15}$/.test(digits) ? `+${digits}` : '';
  }

  private buildPeriodLabel(from: string, to: string) {
    if (!from || !to) return 'Последние 90 дней';
    return `${this.formatShortDate(from)} - ${this.formatShortDate(to)}`;
  }

  private formatShortDate(value: string) {
    const parsed = new Date(value.replace(' ', 'T'));
    if (Number.isNaN(parsed.getTime())) return value;
    const day = String(parsed.getDate()).padStart(2, '0');
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const year = parsed.getFullYear();
    return `${day}.${month}.${year}`;
  }

  private getErrorMessage(error: unknown, fallback: string) {
    const response = (error as { error?: { message?: string } })?.error;
    return response?.message || fallback;
  }
}
